import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { PageHero, SectionHeading } from "../components/shared";
import { SERVICE_OPTIONS, CONTACT } from "../data/content";
import { api, formatError } from "../lib/api";

const Contact = () => {
  const empty = { full_name: "", company: "", job_title: "", email: "", telephone: "", service_required: "", project_location: "", start_date: "", description: "" };
  const [form, setForm] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please provide consent to proceed.");
    setLoading(true);
    try {
      const { data } = await api.post("/enquiries", { ...form, consent: true, source: "Contact Page" });
      toast.success(data.message);
      setForm(empty); setConsent(false);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Submission failed.");
    } finally { setLoading(false); }
  };

  const inputCls = "mt-1.5 w-full border border-[#E2E8F0] rounded-md px-3 py-2.5 focus:border-[#1EA7E1] outline-none";

  return (
    <>
      <PageHero overline="Contact Us" title="Let's Discuss Your Infrastructure Requirement" subtitle="Tell us about your network rollout, maintenance, engineering or technical resource needs." />
      <section className="py-24 bg-white">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading overline="Enquiry Form" title="Request a Capability Discussion" />
            <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-5" data-testid="contact-form">
              <label className="block"><span className="text-sm font-medium">Full name <span className="text-[#F28C28]">*</span></span><input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="contact-name" className={inputCls} /></label>
              <label className="block"><span className="text-sm font-medium">Company</span><input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="contact-company" className={inputCls} /></label>
              <label className="block"><span className="text-sm font-medium">Job title</span><input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} className={inputCls} /></label>
              <label className="block"><span className="text-sm font-medium">Business email <span className="text-[#F28C28]">*</span></span><input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" className={inputCls} /></label>
              <label className="block"><span className="text-sm font-medium">Telephone</span><input value={form.telephone} onChange={(e) => set("telephone", e.target.value)} className={inputCls} /></label>
              <label className="block"><span className="text-sm font-medium">Service required</span>
                <select value={form.service_required} onChange={(e) => set("service_required", e.target.value)} data-testid="contact-service" className={inputCls}>
                  <option value="">Select…</option>{SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block"><span className="text-sm font-medium">Project location</span><input value={form.project_location} onChange={(e) => set("project_location", e.target.value)} className={inputCls} /></label>
              <label className="block"><span className="text-sm font-medium">Expected start date</span><input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} /></label>
              <label className="block sm:col-span-2"><span className="text-sm font-medium">Short project description</span><textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="contact-desc" className={inputCls} /></label>
              <label className="flex items-start gap-2 sm:col-span-2 text-sm text-[#64748B]">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="contact-consent" className="mt-1" />
                I consent to Airanet contacting me regarding this enquiry.
              </label>
              <button disabled={loading} data-testid="contact-submit" className="sm:col-span-2 bg-[#F28C28] text-[#0B1F3A] font-semibold py-3.5 rounded-md hover:bg-[#e07d1c] disabled:opacity-60">{loading ? "Sending…" : "Submit Enquiry"}</button>
            </form>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-[#0B1F3A] text-white rounded-lg p-7">
              <h3 className="font-head font-semibold text-lg text-[#1EA7E1]">Get in Touch</h3>
              <div className="mt-5 space-y-4 text-sm">
                <p className="flex items-start gap-3"><MapPin className="h-5 w-5 text-[#F28C28] shrink-0" /><span><b>Registered / Corporate Office</b><br />{CONTACT.address}</span></p>
                <p className="flex items-center gap-3"><Phone className="h-5 w-5 text-[#F28C28]" /><a href={`tel:${CONTACT.phone}`} className="hover:text-[#1EA7E1]">{CONTACT.phone}</a></p>
                <p className="flex items-center gap-3"><Mail className="h-5 w-5 text-[#F28C28]" /><a href={`mailto:${CONTACT.email}`} className="hover:text-[#1EA7E1]">{CONTACT.email}</a></p>
                <p className="flex items-center gap-3"><Clock className="h-5 w-5 text-[#F28C28]" />Mon – Sat, 9:00 AM – 6:00 PM IST</p>
                <p className="flex items-center gap-3"><MessageCircle className="h-5 w-5 text-[#F28C28]" /><a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="hover:text-[#1EA7E1]">WhatsApp Us</a></p>
              </div>
              <p className="mt-5 pt-5 border-t border-white/10 text-xs text-[#94A3B8]">Registration No: {CONTACT.reg}</p>
            </div>
            <div className="mt-6 rounded-lg overflow-hidden border border-[#E2E8F0]">
              <iframe title="Airanet location" width="100%" height="260" style={{ border: 0 }} loading="lazy"
                src="https://www.google.com/maps?q=Karnal,Haryana&output=embed" />
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default Contact;
